import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { useTheme } from '../../../context/ThemeContext';

const ToggleButton = styled.button`
  position: relative;
  width: 56px;
  height: 28px;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  background: var(--bg-tertiary);
  transition: background-color 0.3s ease;
  padding: 0;
  overflow: hidden;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--primary);
  }

  &:hover {
    opacity: 0.9;
  }
`;

const IconContainer = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
`;

const SunIcon = styled.svg`
  width: 14px;
  height: 14px;
  fill: #fbbf24;
`;

const MoonIcon = styled.svg`
  width: 14px;
  height: 14px;
  fill: #e8eaed;
`;

interface ThemeToggleProps {
  className?: string;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ className }) => {
  const { isDark, toggleTheme } = useTheme();
  const iconRef = useRef<HTMLDivElement>(null);
  const sunRef = useRef<SVGSVGElement>(null);
  const moonRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const icon = iconRef.current;
    const sun = sunRef.current;
    const moon = moonRef.current;

    if (!icon || !sun || !moon) return;

    // Animate position
    gsap.to(icon, {
      left: isDark ? '31px' : '3px',
      duration: 0.3,
      ease: 'power2.out',
    });

    // Animate icons
    gsap.to(sun, {
      opacity: isDark ? 0 : 1,
      scale: isDark ? 0.5 : 1,
      rotation: isDark ? -90 : 0,
      duration: 0.3,
      ease: 'power2.out',
    });

    gsap.to(moon, {
      opacity: isDark ? 1 : 0,
      scale: isDark ? 1 : 0.5,
      rotation: isDark ? 0 : 90,
      duration: 0.3,
      ease: 'power2.out',
    });
  }, [isDark]);

  return (
    <ToggleButton
      className={className}
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      <IconContainer ref={iconRef}>
        <SunIcon
          ref={sunRef}
          viewBox="0 0 24 24"
          style={{ position: 'absolute' }}
        >
          <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
        </SunIcon>
        <MoonIcon
          ref={moonRef}
          viewBox="0 0 24 24"
          style={{ position: 'absolute', opacity: 0 }}
        >
          <path
            fillRule="evenodd"
            d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z"
            clipRule="evenodd"
          />
        </MoonIcon>
      </IconContainer>
    </ToggleButton>
  );
};

export default ThemeToggle;
